/**
 * Supporting files — one card per loan document the workbook can draw on,
 * showing what its structured extract says (`GET /pricing/dscr_calc_evidence`).
 *
 * DISPLAY ONLY. The projection never writes units or the deal, and "Use this
 * value" lives inside a row's drawer where attaching the document is the UW's
 * act — so a card here shows the numbers, it does not offer to apply them.
 *
 * A card list rather than a grid on purpose: each document family carries a
 * different set of fields (a lease has a term and a deposit, an appraisal a
 * market rent and an as-is value, a tax record per-authority lines), so the
 * rows are not one column set. Every file name previews on click.
 */
import { useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { slate, status } from "../../../../theme/colors";

// Which candidates to show per family, in order, and how. A lease's start /
// end collapse into one "Lease term" cell.
const FIELDS = {
  monthlyRent: { label: "Monthly rent", kind: "dollar" },
  securityDeposit: { label: "Security deposit", kind: "dollar" },
  marketRentMonthly: { label: "Market rent / mo", kind: "dollar" },
  asIsValue: { label: "As-is value", kind: "dollar" },
  annualPremium: { label: "Annual premium", kind: "dollar" },
  effectiveDate: { label: "Effective", kind: "date" },
  expirationDate: { label: "Expires", kind: "date" },
  dwellingCoverage: { label: "Dwelling coverage", kind: "dollar" },
  floodCoverage: { label: "Flood coverage", kind: "dollar" },
  annualTaxes: { label: "Annual taxes", kind: "dollar" },
  taxYear: { label: "Tax year", kind: "text" },
};
const ORDER = [
  "monthlyRent",
  "leaseTerm",
  "securityDeposit",
  "marketRentMonthly",
  "asIsValue",
  "annualPremium",
  "effectiveDate",
  "expirationDate",
  "dwellingCoverage",
  "floodCoverage",
  "annualTaxes",
  "taxYear",
];
const fmt = (kind, v) =>
  v == null || v === ""
    ? "—"
    : kind === "dollar"
      ? formatDollar(v, "—")
      : kind === "date"
        ? formatDate(v, String(v))
        : String(v);

// Flags worth a chip, and their tone. `onlyWhen` chips only when true.
const FLAGS = {
  is_arm_length: { label: "Arm's-length", tone: "success", onlyWhen: true },
  is_month_to_month: { label: "Month-to-month", tone: "warning" },
  is_commercial_tenant: { label: "Commercial tenant", tone: "warning" },
  is_single_room_occupancy: { label: "SRO", tone: "warning" },
  has_purchase_option: { label: "Purchase option", tone: "warning" },
  occupancy: { label: (v) => `Occupancy: ${v}`, tone: "neutral" },
  binderOnly: { label: "Binder only", tone: "warning" },
  paidInFull: { label: "Paid in full", tone: "success" },
  floodZone: { label: (v) => `Flood zone ${v}`, tone: "warning" },
  isTaxDelinquent: { label: "Tax delinquent", tone: "error" },
};

const FlagChip = ({ label, tone }) => {
  const t = status[tone] || status.neutral;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 18,
        fontSize: 10.5,
        fontWeight: 700,
        bgcolor: t.bg,
        color: t.fg,
        border: `1px solid ${t.bg}`,
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
};

FlagChip.propTypes = { label: PropTypes.node, tone: PropTypes.string };

const Cell = ({ label, value }) => (
  <Flx column g={0.1} sx={{ minWidth: 0 }}>
    <Txt sx={{ fontSize: 11, color: slate[500] }}>{label}</Txt>
    <Txt sx={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{value}</Txt>
  </Flx>
);

Cell.propTypes = { label: PropTypes.node, value: PropTypes.node };

const docName = (d) => d?.file_display_name || d?.fileName || d?.docType || "Document";

const FileCard = ({ doc, entry, onOpen }) => {
  const cands = entry?.candidates;
  const cells = useMemo(() => {
    const out = [];
    if (!cands) return out;
    ORDER.forEach((key) => {
      if (key === "leaseTerm") {
        const s = cands.leaseStartDate?.value;
        const e = cands.leaseEndDate?.value;
        if (s || e)
          out.push({ key, label: "Lease term", value: `${fmt("date", s)} — ${fmt("date", e)}` });
        return;
      }
      const c = cands[key];
      if (c?.value == null) return;
      out.push({ key, label: FIELDS[key].label, value: fmt(FIELDS[key].kind, c.value) });
    });
    return out;
  }, [cands]);
  const flags = Object.entries(FLAGS)
    .map(([k, cfg]) => {
      const v = entry?.flags?.[k];
      if (v == null || v === false || v === "") return null;
      if (cfg.onlyWhen === true && v !== true) return null;
      return { key: k, label: typeof cfg.label === "function" ? cfg.label(v) : cfg.label, tone: cfg.tone };
    })
    .filter(Boolean);
  const taxLines = Array.isArray(entry?.detail?.taxLineItems) ? entry.detail.taxLineItems : [];
  const meta = [
    doc?.docType || entry?.docType,
    doc?.docGroup || entry?.docGroup,
    doc?.documentStatus || entry?.documentStatus,
    entry?.unitDesignator ? `Unit ${entry.unitDesignator}` : null,
  ].filter(Boolean);

  return (
    <Box sx={{ border: `1px solid ${slate[200]}`, borderRadius: "8px", px: 1.75, py: 1.5, bgcolor: "#fff" }}>
      <Flx ac jb g={2}>
        {/* A displayed file is always clickable → the global preview. */}
        <Txt
          onClick={onOpen}
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: slate[900],
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {docName(doc)}
        </Txt>
        <Txt sx={{ fontSize: 11, color: slate[500], flexShrink: 0 }}>{meta.join(" · ")}</Txt>
      </Flx>

      {cells.length ? (
        <Flx g={3} wrap sx={{ mt: 1 }}>
          {cells.map((c) => (
            <Cell key={c.key} label={c.label} value={c.value} />
          ))}
        </Flx>
      ) : entry ? null : (
        <Txt sx={{ display: "block", fontSize: 11.5, color: slate[500], mt: 0.75 }}>
          No structured extract yet — attach it to a row from the row's drawer.
        </Txt>
      )}

      {taxLines.length ? (
        <Flx g={3} wrap sx={{ mt: 1 }}>
          {taxLines.map((l, i) => (
            <Cell
              key={i}
              label={l?.taxingAuthority || l?.authorityType || "Authority"}
              value={formatDollar(l?.annualAmount, "—")}
            />
          ))}
        </Flx>
      ) : null}

      {flags.length ? (
        <Flx ac g={0.5} wrap sx={{ mt: 1 }}>
          {flags.map((f) => (
            <FlagChip key={f.key} label={f.label} tone={f.tone} />
          ))}
        </Flx>
      ) : null}
    </Box>
  );
};

FileCard.propTypes = { doc: PropTypes.object, entry: PropTypes.object, onOpen: PropTypes.func };

const DscrSupportingFiles = ({ docs, evidenceByDocId }) => {
  const dispatch = useDispatch();
  return (
    <Flx column g={1}>
      {docs.map((d) => (
        <FileCard
          key={d._id}
          doc={d}
          entry={evidenceByDocId?.get(String(d._id)) || null}
          onOpen={() => dispatch(filePreviewSetData(d))}
        />
      ))}
    </Flx>
  );
};

DscrSupportingFiles.propTypes = {
  docs: PropTypes.array,
  evidenceByDocId: PropTypes.instanceOf(Map),
};

export default DscrSupportingFiles;
